import React from 'react'
import { Box, Typography } from '@mui/material'
import { VideoCameraIcon, MicrophoneIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { INTERAX_COLORS } from '../../constants/colors'
import { MediaPermissionState } from '../../../../pure-js/types/AppAssistant'
import { useMediaPermissions } from '../hooks/useMediaPermissions'
import { useTranslation } from '../hooks/useIntractionTranslate'
import { useNavigate, useLocation } from 'react-router-dom'
import { GamerRoutes, FacilitatorRoutes } from '../../config/routes/routeEnums'

interface MediaPermissionStatusProps {
  onPermissionClick?: () => void
  className?: string
}

const getStatusColor = (state: MediaPermissionState): string => {
  switch (state) {
    case MediaPermissionState.GRANTED:
      return INTERAX_COLORS.green
    case MediaPermissionState.DENIED:
      return INTERAX_COLORS.redError
    case MediaPermissionState.ERROR:
      return INTERAX_COLORS.thunderGrey
    default:
      return INTERAX_COLORS.sun
  }
}

const getStatusIcon = (state: MediaPermissionState) => {
  if (state === MediaPermissionState.GRANTED) {
    return <CheckCircleIcon style={{ width: 20, height: 20 }} />
  }
  const isBlocked = state === MediaPermissionState.DENIED || state === MediaPermissionState.ERROR
  if (isBlocked) {
    return <XCircleIcon style={{ width: 20, height: 20 }} />
  }
  return null
}

const getStatusText = (
  state: MediaPermissionState,
  translate: (key: string) => string,
  translationPrefix: string
): string => {
  switch (state) {
    case MediaPermissionState.GRANTED:
      return translate(`${translationPrefix}.permissions.enabled`)
    case MediaPermissionState.DENIED:
      return translate(`${translationPrefix}.permissions.denied`)
    case MediaPermissionState.ERROR:
      return translate(`${translationPrefix}.permissions.error`)
    case MediaPermissionState.CHECKING:
      return translate(`${translationPrefix}.permissions.checking`)
    default:
      return translate(`${translationPrefix}.permissions.prompt`)
  }
}

export const MediaPermissionStatus: React.FC<MediaPermissionStatusProps> = ({ onPermissionClick, className = '' }) => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { camera, microphone, isLoading } = useMediaPermissions()

  // Detect if we're in facilitator or gamer context
  const isFacilitatorContext = location.pathname.startsWith('/facilitator')
  // Facilitator uses 'profile' for profile page, gamer uses 'accountSettings'
  const translationPrefix = isFacilitatorContext ? 'facilitator.profile' : 'gamer.accountSettings'

  const handleClick = () => {
    if (onPermissionClick) {
      onPermissionClick()
    } else {
      // Default: navigate to permissions instructions page
      navigate(isFacilitatorContext ? FacilitatorRoutes.PERMISSIONS_INSTRUCTIONS : GamerRoutes.PERMISSIONS_INSTRUCTIONS)
    }
  }

  const renderPermissionItem = (icon: React.ReactNode, label: string, state: MediaPermissionState) => {
    const color = getStatusColor(state)
    const StatusIcon = getStatusIcon(state)
    const statusText = getStatusText(state, translate, translationPrefix)
    const isGranted = state === MediaPermissionState.GRANTED
    const isClickable = !isGranted

    return (
      <Box
        onClick={isClickable ? handleClick : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px',
          borderRadius: '8px',
          backgroundColor: INTERAX_COLORS.white04,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'background-color 0.2s ease',
          opacity: isGranted ? 0.7 : 1,
          '&:hover': isClickable
            ? {
                backgroundColor: INTERAX_COLORS.white10
              }
            : {}
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {icon}
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: INTERAX_COLORS.white
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {StatusIcon ? <Box sx={{ color }}>{StatusIcon}</Box> : null}
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color,
              padding: '4px 12px',
              borderRadius: '12px',
              backgroundColor: `${color}33`
            }}
          >
            {statusText}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box className={className}>
        <Typography
          sx={{
            fontSize: '14px',
            color: INTERAX_COLORS.thunderGrey
          }}
        >
          {translate(`${translationPrefix}.permissions.checking`)}
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {renderPermissionItem(
        <VideoCameraIcon style={{ width: 24, height: 24, color: INTERAX_COLORS.white }} />,
        translate(`${translationPrefix}.permissions.camera`),
        camera
      )}
      {renderPermissionItem(
        <MicrophoneIcon style={{ width: 24, height: 24, color: INTERAX_COLORS.white }} />,
        translate(`${translationPrefix}.permissions.microphone`),
        microphone
      )}
    </Box>
  )
}
